
import { transports } from 'winston'
import { merge } from 'lodash'
import posthog from 'posthog-js'

// Winston levels mapped to the closest PostHog/OTel severity.
const LEVEL_MAP: Record<string, 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal'> = {
	error: 'error',
	warn: 'warn',
	info: 'info',
	http: 'info',
	verbose: 'debug',
	debug: 'debug',
	silly: 'trace'
}

const SPLAT = Symbol.for('splat')

/**
 * Finds the Error a record was written about, from the extra arguments of
 * `logger.log(level, msg, ...args)` that winston keeps on the splat: the first
 * argument that is itself an Error, or the first object argument whose `error` key
 * is one, whichever comes first. Anything else — an Error under some other key, a
 * string rejection reason, plain metadata — is not an exception. The splat is used
 * rather than the formatted record because the logger's error formatter has already
 * expanded every Error on the record into a plain object; only the splat still holds
 * the original instance whose stack posthog parses into frames.
 *
 * `logger.error(error)` — the Error *is* the record — has no splat, and by the time
 * it reaches a transport it is that serialized copy. An Error is rebuilt from the
 * copy's name/message/stack so posthog still gets frames rather than a generic blob.
 */
const findError = (info: any): Error | undefined => {
	const splat = info[SPLAT]
	if (Array.isArray(splat)) {
		for (const argument of splat) {
			if (argument instanceof Error) return argument
			if (argument?.error instanceof Error) return argument.error
		}
		return undefined
	}
	if (typeof info.stack === 'string') {
		const error = new Error(typeof info.message === 'string' ? info.message : JSON.stringify(info.message))
		if (typeof info.name === 'string') error.name = info.name
		error.stack = info.stack
		return error
	}
	return undefined
}

/**
 * Winston transport that ships log records to PostHog Logs (OpenTelemetry-native)
 * via the posthog-js SDK. Records are batched by the SDK and sent to the
 * project's /i/v1/logs OTLP endpoint, automatically associated with the
 * identified PostHog person/session.
 *
 * Records logged with an Error argument (or an `{ error }` object) are also
 * reported to PostHog error tracking, so a `logger.error("…", error)` is the
 * single call site — no parallel `posthog.captureException` needed.
 */
export class PostHogTransport extends transports.Console {
	private defaultProps: Record<string, string | number | boolean | string[] | number[] | null | undefined> = {}

	constructor(private serviceName: string) {
		super({ silent: false })
	}

	setUserId(id?: string | number) {
		this.defaultProps = merge({}, this.defaultProps, { posthogDistinctId: id?.toString() })
	}

	get name() {
		return 'posthog'
	}

	set name(_value: string) {}

	log(info: any, callback: () => void) {
		try {
			const { level, message, timestamp: _timestamp, ...rest } = info
			const attributes = {
				'service.name': this.serviceName,
				...this.defaultProps,
				...rest
			}
			posthog.captureLog({
				body: typeof message === 'string' ? message : JSON.stringify(message),
				level: LEVEL_MAP[level] || 'info',
				attributes
			})
			this.captureException(info, level, attributes)
		} catch (_err) {
			// Do nothing — logging must never crash the app
		}
		callback?.()
		this.emit('logged', info)
	}

	/**
	 * Reports the Error a record was logged with to PostHog error tracking. The SDK
	 * attaches the identified person and session itself. Every other field on the
	 * record — the same set shipped as the log line's attributes — is forwarded as a
	 * property, so the issue holds the full context the log line had.
	 */
	private captureException(info: any, level: string, attributes: Record<string, any>) {
		try {
			const error = findError(info)
			if (!error) return
			posthog.captureException(error, {
				...attributes,
				log_level: level,
				log_message: info.message
			})
		} catch (_err) {
			// Do nothing — logging must never crash the app
		}
	}
}
